import { ConvexHttpClient } from "convex/browser";
import {
  convexToJson,
  jsonToConvex,
  type JSONValue,
  type Value,
} from "convex/values";

let client: ConvexHttpClient | undefined;
let clientBaseUrl: string | undefined;

export type ConvexPublicServerEndpointEnvironment = Readonly<{
  bundledServerUrl?: string;
  convexUrl?: string;
  isVercel?: boolean;
  nextPublicConvexUrl?: string;
  selfHostedUrl?: string;
}>;

/**
 * Public query paths used by the landing-page server runtime.
 *
 * Keep these as plain strings in the production bundle. Vercel's function
 * compiler has failed before issuing `fetch` while resolving generated Convex
 * function references. A test below this module's boundary verifies that these
 * values still match the generated API references.
 */
export const convexPublicQueryPaths = {
  listPilotCustomUrlSearchIndexingSitemap:
    "domains/profiles/customUrl/queries:listPilotCustomUrlSearchIndexingSitemap",
  resolvePublicProfileBySlug:
    "domains/profiles/customUrl/queries:resolvePublicProfileBySlug",
} as const;

function firstConfiguredUrl(
  ...candidates: ReadonlyArray<string | undefined>
): string | undefined {
  for (const candidate of candidates) {
    const normalized = candidate?.trim();
    if (normalized) return normalized;
  }
  return undefined;
}

/**
 * Select the server-side public-query endpoint without letting a Vercel-only
 * integration variable shadow the endpoint compiled into the same deployment.
 */
export function resolveConvexPublicServerEndpointUrl({
  bundledServerUrl,
  convexUrl,
  isVercel = false,
  nextPublicConvexUrl,
  selfHostedUrl,
}: ConvexPublicServerEndpointEnvironment): string | undefined {
  if (isVercel) {
    return firstConfiguredUrl(
      bundledServerUrl,
      nextPublicConvexUrl,
      selfHostedUrl,
      convexUrl,
    );
  }

  return firstConfiguredUrl(
    selfHostedUrl,
    convexUrl,
    bundledServerUrl,
    nextPublicConvexUrl,
  );
}

/**
 * Convex endpoint for anonymous public queries (/p/[slug], sitemap).
 *
 * In the devcontainer, Next.js SSR runs inside Docker while `.env.local` often
 * points at `http://127.0.0.1:3210` for host-browser access. Prefer compose
 * service URLs on the server (`CONVEX_SELF_HOSTED_URL` / `CONVEX_URL`).
 */
export function resolveConvexPublicEndpointUrl(): string | undefined {
  if (typeof window === "undefined") {
    return resolveConvexPublicServerEndpointUrl({
      bundledServerUrl: process.env.DRONEJOB_PUBLIC_CONVEX_SERVER_URL,
      convexUrl: process.env.CONVEX_URL,
      isVercel: process.env.VERCEL === "1",
      nextPublicConvexUrl: process.env.NEXT_PUBLIC_CONVEX_URL,
      selfHostedUrl: process.env.CONVEX_SELF_HOSTED_URL,
    });
  }

  return process.env.NEXT_PUBLIC_CONVEX_URL;
}

export function getConvexPublicClient(): ConvexHttpClient | null {
  const convexUrl = resolveConvexPublicEndpointUrl();
  if (!convexUrl) {
    reportPublicConvexClientFailure("endpoint_missing");
    return null;
  }

  try {
    if (!client || clientBaseUrl !== convexUrl) {
      client = new ConvexHttpClient(convexUrl);
      clientBaseUrl = convexUrl;
    }
    return client;
  } catch (error) {
    reportPublicConvexClientFailure("client_initialization_failed", error);
    return null;
  }
}

/**
 * Execute an anonymous public query from a Next.js server function.
 *
 * Vercel's bundled server runtime has repeatedly returned before
 * ConvexHttpClient issues a network request, even though the same client and
 * endpoint work in Node and in the browser. Keep browser behavior on the SDK,
 * but make the SSR transport explicit and compatible with Convex's documented
 * HTTP query wire format. Resolve the server endpoint directly: provider
 * compilation must not choose this transport through a `typeof window` branch.
 */
export async function queryConvexPublicServer<Result>(
  queryPath: string,
  args: Record<string, Value | undefined>,
): Promise<Result> {
  const convexUrl = resolveConvexPublicServerEndpointUrl({
    bundledServerUrl: process.env.DRONEJOB_PUBLIC_CONVEX_SERVER_URL,
    convexUrl: process.env.CONVEX_URL,
    isVercel: process.env.VERCEL === "1",
    nextPublicConvexUrl: process.env.NEXT_PUBLIC_CONVEX_URL,
    selfHostedUrl: process.env.CONVEX_SELF_HOSTED_URL,
  });
  if (!convexUrl) {
    reportPublicConvexServerFailure("endpoint_missing");
    throw new Error("Public Convex endpoint is unavailable");
  }

  try {
    const response = await fetch(`${convexUrl.replace(/\/$/, "")}/api/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path: queryPath,
        format: "convex_encoded_json",
        args: [convexToJson(args)],
      }),
      cache: "no-store",
    });

    if (!response.ok && response.status !== 560) {
      throw new Error(`Public Convex query failed with HTTP ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!isConvexQueryResponse(payload)) {
      throw new Error("Public Convex query returned an invalid response");
    }
    if (payload.status !== "success") {
      throw new Error("Public Convex query returned an application error");
    }

    return jsonToConvex(payload.value as JSONValue) as Result;
  } catch (error) {
    reportPublicConvexServerFailure("server_query_failed", error);
    throw error;
  }
}

type ConvexQueryResponse =
  | Readonly<{ status: "success"; value: unknown }>
  | Readonly<{ status: "error" }>;

function isConvexQueryResponse(value: unknown): value is ConvexQueryResponse {
  if (typeof value !== "object" || value === null || !("status" in value)) {
    return false;
  }
  const status = value.status;
  return status === "success" || status === "error";
}

function reportPublicConvexServerFailure(
  reason: string,
  error?: unknown,
): void {
  // This transport is server-only by call-site contract. Do not suppress its
  // diagnostic through a provider-compiled `typeof window` check.
  console.error("PUBLIC_CONVEX_SERVER_QUERY_UNAVAILABLE", {
    errorName: error instanceof Error ? error.name : undefined,
    hasBundledServerUrl: Boolean(
      process.env.DRONEJOB_PUBLIC_CONVEX_SERVER_URL?.trim(),
    ),
    hasConvexUrl: Boolean(process.env.CONVEX_URL?.trim()),
    hasNextPublicConvexUrl: Boolean(process.env.NEXT_PUBLIC_CONVEX_URL?.trim()),
    hasSelfHostedUrl: Boolean(process.env.CONVEX_SELF_HOSTED_URL?.trim()),
    reason,
  });
}

function reportPublicConvexClientFailure(
  reason: string,
  error?: unknown,
): void {
  if (typeof window !== "undefined") return;

  // Log only configuration presence and the error class. Endpoint values,
  // profile arguments, error messages, and stacks are deliberately excluded.
  console.error("PUBLIC_CONVEX_CLIENT_UNAVAILABLE", {
    errorName: error instanceof Error ? error.name : undefined,
    hasBundledServerUrl: Boolean(
      process.env.DRONEJOB_PUBLIC_CONVEX_SERVER_URL?.trim(),
    ),
    hasConvexUrl: Boolean(process.env.CONVEX_URL?.trim()),
    hasNextPublicConvexUrl: Boolean(process.env.NEXT_PUBLIC_CONVEX_URL?.trim()),
    hasSelfHostedUrl: Boolean(process.env.CONVEX_SELF_HOSTED_URL?.trim()),
    reason,
  });
}
